/** Tool execution shape used by chat metadata and server truthfulness guards. */
export type AgenticToolExecutionPlacementSignal = {
  name?: string;
  summary?: string;
  result?: unknown;
};

export const SELECTION_REWRITE_APPLIED_SUMMARY_PATTERN = /generated and applied manuscript rewrite/i;

export function buildSelectionRewriteAppliedToolSummary(generatedChars: number): string {
  return `Generated and applied manuscript rewrite (${generatedChars} chars).`;
}

export function resolveToolExecutionSummary(execution: AgenticToolExecutionPlacementSignal): string {
  if (typeof execution.summary === "string" && execution.summary.trim()) {
    return execution.summary.trim();
  }
  const result = execution.result;
  if (result && typeof result === "object") {
    const summary = (result as { summary?: unknown }).summary;
    if (typeof summary === "string" && summary.trim()) {
      return summary.trim();
    }
  }
  return "";
}

export function toolExecutionSummaryIndicatesSelectionRewriteApplied(summary: string): boolean {
  return SELECTION_REWRITE_APPLIED_SUMMARY_PATTERN.test(summary);
}

/** Synthetic whole-scope rewrite confirmations persisted without toolExecutions metadata. */
const WHOLE_SCOPE_REWRITE_SUCCESS_ASSISTANT_MESSAGE_RE =
  /\bI've rewritten (?:your document|(?:chapter\s+[\w.-]+|the chapter)) as requested\b/i;

/** True when assistant bubble text is the runner's whole-scope rewrite success confirmation. */
export function assistantMessageReportsSuccessfulWholeScopeRewrite(text: string): boolean {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return false;
  const firstLine = trimmed.split(/\n/)[0]?.trim() ?? "";
  return (
    WHOLE_SCOPE_REWRITE_SUCCESS_ASSISTANT_MESSAGE_RE.test(firstLine)
    || WHOLE_SCOPE_REWRITE_SUCCESS_ASSISTANT_MESSAGE_RE.test(trimmed)
  );
}

/** True when manage_project_documents actually wrote manuscript prose (not scan/rename/ambiguity errors). */
export function manageProjectDocumentsSummaryIndicatesManuscriptApplied(summary: string): boolean {
  const normalized = String(summary ?? "").trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === "edited document" || normalized === "edited document batch") return true;
  if (normalized.startsWith("created and populated document")) return true;
  if (normalized.startsWith("reviewed and edited document")) return true;
  if (normalized.startsWith("inserted rewrite into manuscript")) return true;
  return false;
}

/**
 * Selection rewrite fast path applies via `applyMarkdownToDocument` and records
 * `generate_write_button_prose` (not `manage_project_documents`).
 */
export function hasSuccessfulSelectionRewriteManuscriptApply(
  toolExecutions: AgenticToolExecutionPlacementSignal[],
): boolean {
  return toolExecutions.some((execution) => {
    if (execution?.name !== "generate_write_button_prose") return false;
    const result = execution.result as { error?: unknown; data?: unknown } | undefined;
    if (result?.error) return false;
    const summary = resolveToolExecutionSummary(execution);
    if (!toolExecutionSummaryIndicatesSelectionRewriteApplied(summary)) return false;
    return typeof result?.data === "string" && result.data.trim().length > 0;
  });
}
